import { readJson, remove, writeJson } from "../../lib/storage";
import { ModemSession, type Way } from "./modem";

/**
 * The USB caller-ID device on this till pc (kassa-besluiten: "via USB-toestel aan telefoonlijn (zoals
 * nu)"), read through the browser's serial port access (Web Serial: Chrome and Edge on a computer).
 * The device is chosen once in Beheer › Printers & toestellen; after that the till connects by itself
 * at every start and whenever the device is plugged in again. Each number goes to the server, which
 * shows the call on every till of the shop.
 */

/** The parts of the Web Serial API Tafo uses. */
interface PortInfo {
  usbVendorId?: number;
  usbProductId?: number;
}
interface SerialPortLike {
  open(options: { baudRate: number }): Promise<void>;
  close(): Promise<void>;
  /** Bytes from the device (Uint8Array chunks), straight into a text decoder. */
  readonly readable: ReadableStream<BufferSource> | null;
  readonly writable: WritableStream<Uint8Array> | null;
  getInfo(): PortInfo;
  forget?: () => Promise<void>;
  setSignals?: (signals: { dataTerminalReady?: boolean; requestToSend?: boolean }) => Promise<void>;
}
interface SerialLike extends EventTarget {
  getPorts(): Promise<SerialPortLike[]>;
  requestPort(options?: object): Promise<SerialPortLike>;
}

export type CallerIdStatus =
  /** This browser cannot read a USB device (only Chrome or Edge on a computer can). */
  | "unsupported"
  /** No device chosen on this till. */
  | "off"
  | "connecting"
  /** Connected and the device answers: calls come in. */
  | "on"
  /** Opened, but the device does not answer: not a caller-ID modem, or it is stuck. */
  | "silent"
  /** Chosen, but not plugged in. */
  | "absent"
  /** Another program has the device open (e.g. the old till software). */
  | "busy";

/** Where the device's numbers go, and the way of listening the shop's devices share (the server). */
export interface CallerIdShop {
  report: (digits: string) => void;
  /** The way that gave a number on a till of the shop; null when none did yet. */
  way: () => Promise<number | null>;
  /** This device gave a number this way. */
  proven: (country: number) => void;
}

/** Which device this till listens to (per browser, so per till pc). */
const KEY = "tafo.callerId";
/** The way of listening that worked for the device of this till (or the one being tried). */
const WAY_KEY = "tafo.callerId.way";
/** USB modems ignore the speed; 9600 is what a serial modem expects. */
const BAUD_RATE = 9_600;

/** A device that did not answer by now is called silent. */
const SILENT_MS = 8_000;
/** How many lines of the device Beheer can show. */
const LOG_LINES = 40;

/** A line to or from the device, for the person who sets it up. */
export interface DeviceLine {
  at: number;
  direction: "out" | "in";
  text: string;
}

const serialApi = (): SerialLike | null => (navigator as Navigator & { serial?: SerialLike }).serial ?? null;
const savedPort = () => readJson<PortInfo | null>(KEY, null);
const samePort = (port: SerialPortLike, saved: PortInfo) => {
  const info = port.getInfo();
  return info.usbVendorId === saved.usbVendorId && info.usbProductId === saved.usbProductId;
};

class CallerIdDevice {
  private status: CallerIdStatus = serialApi() ? "off" : "unsupported";
  private readonly listeners = new Set<() => void>();
  private shop: CallerIdShop | null = null;
  /** The till wants to listen: between start() and stop(). */
  private wanted = false;
  private watching = false;
  private port: SerialPortLike | null = null;
  private reader: ReadableStreamDefaultReader<string> | null = null;
  /** The listening of the open device; resolves once it is closed again. */
  private running: Promise<void> | null = null;

  /** The last lines to and from the device; a new list at every change. */
  private log: readonly DeviceLine[] = [];

  readonly getStatus = (): CallerIdStatus => this.status;
  readonly getLog = (): readonly DeviceLine[] => this.log;

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Listens with the device chosen earlier on this till; numbers go to the shop. */
  async start(shop: CallerIdShop): Promise<void> {
    this.shop = shop;
    this.wanted = true;
    const serial = serialApi();
    if (!serial) return;
    if (!this.watching) {
      this.watching = true;
      // plugged in again: listen again (pulled out: the listening ends by itself)
      serial.addEventListener("connect", () => {
        if (this.wanted) void this.connect(serial);
      });
    }
    await this.connect(serial);
  }

  /** Stops listening (the till closes); the choice of device stays. */
  async stop(): Promise<void> {
    this.wanted = false;
    await this.close();
  }

  /** "Toestel kiezen": the browser shows the devices it can open (needs a click of the person). */
  async choose(): Promise<boolean> {
    const serial = serialApi();
    if (!serial) return false;
    let port: SerialPortLike;
    try {
      port = await serial.requestPort();
    } catch {
      return false; // closed without choosing
    }
    const wanted = this.wanted;
    this.wanted = false;
    await this.close();
    this.wanted = wanted;
    writeJson(KEY, port.getInfo());
    // a device chosen (again): its line may need another way of listening than the one this till
    // remembered (Pita Antalya, 11/10/2026: a second line, RING without a number, nothing else tried)
    remove(WAY_KEY);
    this.running = this.listen(port);
    return true;
  }

  /** "Ontkoppelen": this till no longer listens to a device. */
  async forget(): Promise<void> {
    const port = this.port;
    const wanted = this.wanted;
    this.wanted = false;
    await this.close();
    this.wanted = wanted;
    remove(KEY);
    remove(WAY_KEY);
    await port?.forget?.().catch(() => undefined);
    this.set(serialApi() ? "off" : "unsupported");
  }

  private set(status: CallerIdStatus): void {
    if (status === this.status) return;
    this.status = status;
    for (const listener of this.listeners) listener();
  }

  private note(direction: DeviceLine["direction"], text: string): void {
    this.log = [...this.log, { at: Date.now(), direction, text: text.slice(0, 80) }].slice(-LOG_LINES);
    for (const listener of this.listeners) listener();
  }

  private async connect(serial: SerialLike): Promise<void> {
    if (this.running) return;
    const saved = savedPort();
    if (saved === null) {
      this.set("off");
      return;
    }
    const port = (await serial.getPorts()).find((candidate) => samePort(candidate, saved));
    if (!port) this.set("absent");
    // another connect may have opened it while the ports were listed
    else this.running ??= this.listen(port);
  }

  private async close(): Promise<void> {
    await this.reader?.cancel().catch(() => undefined);
    await this.running;
  }

  /**
   * The way this till listens: what it remembered, otherwise the way that gave a number on another till
   * of the shop (to be proven on this line too), otherwise the first.
   */
  private async startWay(): Promise<Way> {
    const own = readJson<Way | null>(WAY_KEY, null);
    if (own !== null) return own;
    const shop = await this.shop?.way().catch(() => null);
    return { country: shop ?? 0, proven: false };
  }

  private async listen(port: SerialPortLike): Promise<void> {
    this.set("connecting");
    try {
      await port.open({ baudRate: BAUD_RATE });
    } catch {
      this.running = null;
      this.set("busy");
      return;
    }
    if (!port.readable || !port.writable) {
      await port.close().catch(() => undefined);
      this.running = null;
      this.set("busy");
      return;
    }
    this.port = port;
    this.log = [];
    // a modem only talks to a computer that says it is there (DTR) and ready (RTS)
    await port.setSignals?.({ dataTerminalReady: true, requestToSend: true }).catch(() => undefined);
    // "Verbonden" only once the device answered: opening a port says nothing about what hangs on it
    const silent = setTimeout(() => {
      if (this.status === "connecting") this.set("silent");
    }, SILENT_MS);
    const way = await this.startWay();
    // a way proven on this till before the shop kept one: the shop learns it now
    if (way.proven) this.shop?.proven(way.country);
    const decoder = new TextDecoderStream();
    const piped = port.readable.pipeTo(decoder.writable).catch(() => undefined);
    const reader = decoder.readable.getReader();
    this.reader = reader;
    const writer = port.writable.getWriter();
    const encoder = new TextEncoder();
    const session = new ModemSession(
      (text) => {
        writer.write(encoder.encode(text)).catch(() => undefined);
      },
      (digits) => {
        this.set("on");
        this.shop?.report(digits);
      },
      (direction, text) => {
        this.note(direction, text);
      },
      () => {
        this.set("on");
      },
      way,
      (next) => {
        writeJson(WAY_KEY, next);
        if (next.proven) this.shop?.proven(next.country);
      },
    );
    session.start();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        session.feed(value);
      }
    } catch {
      // pulled out: listening stops until it is plugged in again
    } finally {
      clearTimeout(silent);
      session.stop();
      reader.releaseLock();
      writer.releaseLock();
      await piped;
      await port.close().catch(() => undefined);
      this.port = null;
      this.reader = null;
      this.running = null;
      this.set(savedPort() === null ? "off" : "absent");
      // e.g. the till screen was opened again meanwhile: listen again
      const serial = serialApi();
      if (this.wanted && serial) void this.connect(serial);
    }
  }
}

/** One device per till pc. */
export const callerIdDevice = new CallerIdDevice();
